import { createSlice } from '@reduxjs/toolkit';
import { ENUM__string_id_empty } from '@shared/constants/ENUM__string_id_empty';
import { DTO__generation } from '@shared/dto/generation/DTO__generation';
import { FIELD__image_asset } from '@shared/dto/generation/graph_invoke/_fields/image/FIELD__image_asset';
import { DTO__user_task } from '@shared/dto/user_task/DTO__user_task';
import { ENUM__redux_action } from '../_base/ENUM__redux_action';
import { ENUM__redux_slice } from '../_base/ENUM__redux_slice';
import { LOCAL__global_reset } from '../_base/LOCAL__global_reset';
import { INIT_REMOTE__image_asset_segment } from '../images/segment/REMOTE__image_asset_segment';
import { INIT_REMOTE__images_file_data_upload_to_workspace } from '../images/upload/REMOTE__images_file_data_upload_to_workspace';
import { INIT_REMOTE__images_use_in_non_custom_collection } from '../images/upload/REMOTE__images_use_in_non_custom_collection';
import { init_local_generation_upsert_generation } from './entity/LOCAL__generation_upsert_generation';
import { INIT_REMOTE__generation_create } from './entity/REMOTE__generation_create';
import { INIT_REMOTE__generation_fields_store_clean } from './entity/REMOTE__generation_fields_store_clean';
import { init_remote_generation_duration_adjusting_add } from './entity/remote_generation_duration_adjusting_add';
import { INIT_LOCAL__generation_set_field_image_to_edit } from './image/LOCAL__generation_set_field_image_to_edit';
import { INIT_LOCAL__generation_set_current } from './LOCAL__generation_set_current';
import { INIT_LOCAL__generation_set_generation_id_next } from './LOCAL__generation_set_generation_id_next';
import { INIT_REMOTE__generation_update_graph } from './settings/REMOTE__generation_update_graph';
import { INIT_REMOTE__generation_update_tool } from './settings/REMOTE__generation_update_tool';
import { INIT_REMOTE__generation_update_tool_name } from './settings/REMOTE__generation_update_tool_name';
import { STATE__generation } from './STATE__generation';
import { INIT_REMOTE__generation_start } from './status/REMOTE__generation_start';
import { INIT_REMOTE__generation_start_with_new_seeds } from './status/REMOTE__generation_start_with_new_seeds';
import { INIT_REMOTE__generation_stop } from './status/REMOTE__generation_stop';
import { INIT_REMOTE__generation_update_status_lock } from './status/REMOTE__generation_update_status_lock';
import { INIT_REMOTE__generation_update_visibility } from './status/REMOTE__generation_update_visibility';
import { init_local_user_task_update_without_generations } from './user_task/entity/init_local_user_task_update_without_generations';
import { INIT_LOCAL__user_task_upsert } from './user_task/entity/LOCAL__user_task_upsert';
import { init_local_user_task_delete } from './user_task/entity/local_user_task_delete';
import { INIT_REMOTE__user_task_load_generations_by_generation_id } from './user_task/entity/REMOTE__user_task_load_generations_by_generation_id';
import { INIT_REMOTE__user_task_load_generations_by_user_task_id } from './user_task/entity/REMOTE__user_task_load_generations_by_user_task_id';
import { init_remote_user_task_create } from './user_task/entity/remote_user_task_create';
import { init_remote_user_task_duplicate } from './user_task/entity/remote_user_task_duplicate';
import { init_remote_user_task_get_shared_by_generation_id } from './user_task/entity/remote_user_task_get_shared_by_generation_id';
import { init_remote_user_task_update_visibility } from './user_task/entity/remote_user_task_update_visibility';
import { INIT_REMOTE__generation_create_for_field_consumer } from './user_task/REMOTE__generation_create_for_field_consumer';

const initialState: STATE__generation = {
  user_tasks_map: new Map(),
  generations_map: new Map(),

  user_task_reference_entities_map: new Map(),

  generation_current: DTO__generation.CREATE_STUB(),
  user_task_current: DTO__user_task.CREATE_STUB(),

  user_task_load_generations_in_progress: new Set(),

  generation_next_id: ENUM__string_id_empty.OBJECT_ID,

  generation_ids_with_locked_status: new Set(),

  field_image_to_edit: FIELD__image_asset.CREATE_STUB(),
};

const reducers: Record<string, any> = {};
reducers[ENUM__redux_action.GENERATION_SET_FIELD_IMAGE_TO_EDIT.remove_first_part_of_path()] = INIT_LOCAL__generation_set_field_image_to_edit;
reducers[ENUM__redux_action.GENERATION_SET_NEXT.remove_first_part_of_path()] = INIT_LOCAL__generation_set_generation_id_next;

export const slice_generation = createSlice({
  name: ENUM__redux_slice.GENERATION,
  initialState,
  reducers: {
    ...reducers,
    user_task_upsert: INIT_LOCAL__user_task_upsert,
    user_task_update_without_generations: init_local_user_task_update_without_generations,
    user_task_delete: init_local_user_task_delete,

    setCurrent: INIT_LOCAL__generation_set_current,
    upsert: init_local_generation_upsert_generation,
  },
  extraReducers: builder => {
    init_remote_user_task_create(builder);
    init_remote_user_task_update_visibility(builder);
    init_remote_user_task_get_shared_by_generation_id(builder);
    init_remote_user_task_duplicate(builder);

    INIT_REMOTE__user_task_load_generations_by_user_task_id(builder);
    INIT_REMOTE__user_task_load_generations_by_generation_id(builder);

    INIT_REMOTE__generation_create(builder);

    INIT_REMOTE__generation_update_graph(builder);

    INIT_REMOTE__generation_update_tool(builder);
    INIT_REMOTE__generation_update_tool_name(builder);

    INIT_REMOTE__generation_start(builder);
    INIT_REMOTE__generation_start_with_new_seeds(builder);
    INIT_REMOTE__generation_stop(builder);
    INIT_REMOTE__generation_update_status_lock(builder);
    INIT_REMOTE__generation_update_visibility(builder);

    INIT_REMOTE__image_asset_segment(builder);

    INIT_REMOTE__images_use_in_non_custom_collection(builder);
    INIT_REMOTE__images_file_data_upload_to_workspace(builder);

    INIT_REMOTE__generation_create_for_field_consumer(builder);

    INIT_REMOTE__generation_fields_store_clean(builder);

    init_remote_generation_duration_adjusting_add(builder);

    builder.addCase(LOCAL__global_reset, state => {
      return initialState;
    });
  },
});
